import { IntlShape } from 'react-intl';
import { ReactComponent as IconExit } from '@whitelabel/media/icons/small/exit.svg';
import { ReactComponent as IconArrowLeft } from '@whitelabel/media/icons/small/arrow-left.svg';
import messages from './messages/commonMsg';
import { isRCAffiliateLink } from './site';

export interface INavParent {
  key: string;
  name: string;
  children: INavParent[] | INavLink[];
}

export interface INavLink {
  key: string;
  name: string;
  href: string;
}

export const isNavParent = (nav: INavParent | INavLink): nav is INavParent =>
  (nav as INavParent).children !== undefined;

export const isNavLink = (nav: INavParent | INavLink): nav is INavLink => (nav as INavLink).href !== undefined;

const helpArticleDropdownLinks = (intl: IntlShape): INavParent => ({
  key: 'helparticle',
  name: intl.formatMessage(messages.helpArticleLinks),
  children: [
    {
      key: 'excesscover',
      href: `/excess-cover`,
      name: intl.formatMessage(messages.explainSubmenu),
    },
    {
      key: 'howitworks',
      href: `/how-it-works`,
      name: intl.formatMessage(messages.howItWorksSubmenu),
    },
    {
      key: 'whyuserentalcover',
      href: `/help/about-your-cover/why-use-rentalcover`,
      name: intl.formatMessage(messages.whySubmenu),
    },
  ],
});

const rentalGuideDropdownLinks = (intl: IntlShape): INavParent => ({
  key: 'rentalguide',
  name: intl.formatMessage(messages.rentalGuideLinks),
  children: [
    {
      key: 'rentalguide-us',
      href: `/rental-guides/car-rental-insurance-united-states`,
      name: 'United States',
    },
    {
      key: 'rentalguide-uk',
      href: `/rental-guides/car-rental-insurance-united-kingdom`,
      name: 'United Kingdom',
    },
    {
      key: 'rentalguide-au',
      href: `/rental-guides/car-rental-insurance-australia`,
      name: 'Australia',
    },
    {
      key: 'rentalguide-ca',
      href: `/rental-guides/car-rental-insurance-canada`,
      name: 'Canada',
    },
    {
      key: 'rentalguide-cn',
      href: `/rental-guides/car-rental-insurance-china`,
      name: 'China',
    },
    {
      key: 'rentalguide-fr',
      href: `/rental-guides/car-rental-insurance-france`,
      name: 'France',
    },
    {
      key: 'rentalguide-it',
      href: `/rental-guides/car-rental-insurance-italy`,
      name: 'Italy',
    },
    {
      key: 'rentalguide-ja',
      href: `/rental-guides/car-rental-insurance-japan`,
      name: 'Japan',
    },
    {
      key: 'rentalguide-mx',
      href: `/rental-guides/car-rental-insurance-mexico`,
      name: 'Mexico',
    },
    {
      key: 'rentalguide-row',
      href: `/rental-guides`,
      name: 'Worldwide',
    },
  ],
});

export const howItWorksDropdownLinks = (intl: IntlShape): INavParent => ({
  key: 'howitworks',
  name: intl.formatMessage(messages.howItWorksDropdownLinks),
  children: [helpArticleDropdownLinks(intl), rentalGuideDropdownLinks(intl)],
});

export const protectionLink = (intl: IntlShape) => ({
  key: 'protection',
  messageKey: 'yourProtection',
  path: isRCAffiliateLink() ? '/protection' : '/account',
  name: intl.formatMessage(messages.yourProtectionLinkText),
  'data-test-id': 'nav-item-protection',
});

export const profileLink = (intl: IntlShape) => ({
  key: 'profile',
  messageKey: 'yourProfile',
  path: '/account/profile',
  name: intl.formatMessage(messages.yourProfileLinkText),
  'data-test-id': 'nav-item-profile',
});

export const claimsLink = (intl: IntlShape) => ({
  key: 'claims',
  messageKey: 'yourClaims',
  path: '/claims',
  name: intl.formatMessage(messages.yourClaimsLinkText),
  'data-test-id': 'nav-item-claims',
});

export const offersLink = (intl: IntlShape) => ({
  key: 'offers',
  messageKey: 'yourOffers',
  path: '/account/offers',
  name: intl.formatMessage(messages.yourOffersLinkText),
  extraLabel: intl.formatMessage(messages.yourOffersExtraLabel),
  'data-test-id': 'nav-item-offers',
});

export const claimDetailsLink = (intl: IntlShape, claimReference: string) => ({
  key: 'claimDetails',
  messageKey: 'yourClaimDetails',
  path: `/claims/${claimReference}/details`,
  name: intl.formatMessage(messages.yourClaimsLinkText),
  icon: IconArrowLeft,
  'data-test-id': 'nav-item-claim-details',
});

export const logoutLink = (intl: IntlShape) => ({
  key: 'logout',
  messageKey: 'logout',
  path: '/logout-customer',
  dataAnalytics: 'page.navbar.btn.logout',
  icon: IconExit,
  name: intl.formatMessage(messages.logoutLinkText),
  'data-test-id': 'nav-item-logout',
});
